import { Dialog, DialogContent, Grid } from '@material-ui/core';
import axios from 'axios';
import { FastField, Field, Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import * as React from 'react';
import { FaSpinner } from 'react-icons/fa';
import styled from 'styled-components';
import { mixed, object, string } from 'yup';
import FirstName, { firstNameValidation } from './form/FirstName';
import LastName, { lastNameValidation } from './form/LastName';
import PrimaryPhoneNumber, { primaryPhoneNumberValidation } from './form/PrimaryPhoneNumber';
import EmailAddress, { emailAddressValidation } from './form/EmailAddress';
import Address, { addressValidation } from './form/Address';
import City, { cityValidation } from './form/City';
import State, { stateValidation } from './form/State';
import PostalCode, { postalCodeValidation } from './form/PostalCode';
import CurrentEmployer, { currentEmployerValidation } from './form/CurrentEmployer';
import LinkedInUrl from './form/LinkedInUrl';
import GitHubUrl from './form/GitHubUrl';
import PortfolioUrl from './form/PortfolioUrl';
import OtherUrl from './form/OtherUrl';
import HearAboutMk from './form/HearAboutMk';
import OtherSpecify from './form/OtherSpecify';
import WhyMk from './form/WhyMk';
import AngleRight from '../components/AngleRight';

const SUPPORTED_FORMATS = [
	'application/pdf',
	'text/plain',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const FormWrapper = styled.div`
	margin-top: 2em;
	position: relative;

	& .form-container {
		max-width: 60rem;
		padding: 1em 0;
		position: relative;
	}

	& .form {
		// background-color: #f9fafa;
		// border: 1px solid #ddd;
		// margin: 0 auto;
		max-width: 55rem;
		// padding: 2em 0;
		// position: relative;
		// z-index: 1;
	}

	& .form-shadow {
		background-color: #34495e;
		height: 100%;
		left: 4.5em;
		position: absolute;
		top: 0;
		width: 90%;
		z-index: 0;
	}

	& .input-section {
		font-weight: bold;
		margin: 1em 0;
	}

	& .resume-label {
		display: block;
		margin: 10px 0;
	}

	& .resume-button {
		padding: 9px 0 9px 9px;
		background-color: ${(props) => props.theme.inputBackgroundColor};
		margin-bottom: 40px;

		& input {
			border: none;
			width: 100%;
		}

		& .validation-message {
			color: ${(props) => props.theme.red};
		}
	}

	& fieldset.radioButtons {
		border: 0;
		margin-top: 0.5em;
		margin-bottom: 1.5em;
	}

	& .primary-button {
		margin: 1em 0;
		max-width: none;
	}


	& .grid {
		@media (${(props) => props.theme.largeUp}) {
			justify-content: space-between;
			display: grid;
			grid-column-gap: 2em;
		}
	}


	& .grid.two-columns {
		@media (${(props) => props.theme.largeUp}) {
			grid-template-columns: 1fr 1fr;
		}
	}

	& .grid.three-columns {
		@media (${(props) => props.theme.largeUp}) {
			grid-template-columns: 1fr 1fr 1fr;
		}
	}
`;

const SpinnerContainer = styled.p`
	text-align: center;

	& .fa-spin {
		font-size: 3rem;
		animation: fa-spin 2s infinite linear;
	}

	@keyframes fa-spin {
		0%
		{ transform: rotate(0deg); }
		100%
		{ transform: rotate(360deg); }
	}
`;

interface ApplicationFormProps {
	legacyEndpoint: string;
	position: string;
}

interface ApplicationFormState {
	applicationId: string;
	isDialogOpened: boolean;
	isError: boolean;
}

/* tslint:disable:jsx-no-lambda jsx-boolean-value no-magic-numbers */
class ApplicationForm extends React.Component<ApplicationFormProps, ApplicationFormState> {
	state: ApplicationFormState = {
		applicationId: '',
		isDialogOpened: false,
		isError: false,
	};

	closeDialog = () => {
		this.setState({ isDialogOpened: false });
	}

	createFileInput = (props: any) => {
		const {
			form: { errors },
		} = props;

		return <React.Fragment>
			<input
				name='resume'
				id='resume'
				type='file'
				onChange={(event: any) => {
					props.form.setFieldValue('resume', (event.currentTarget as HTMLInputElement).files[0]);
				}}
			/>
			<div className='validation-message'>{errors['resume']}</div>
		</React.Fragment>;
	}

	onSubmit = (data: any) => {
		this.setState({ isDialogOpened: true, isError: false });
		const submitAppEndpoint = `${this.props.legacyEndpoint}careers-page-application/`;

		if (data.resume) {
			const resumeFile = data.resume;
			data.resume = data.resume.name;

			axios.post(submitAppEndpoint,
				data, {
					headers: { 'content-type': 'application/json' },
				})
				.then((apiResult) => {
					return {
						url: apiResult.data.url,
						applicationId: apiResult.data.id,
					};
				})
				.then((result) => {
					this.setState({ applicationId: result.applicationId });

					let contentType;
					if (resumeFile.type === 'application/pdf') {
						contentType = 'application/octet-stream';
					}
					else {
						contentType = 'text/plain';
					}

					return axios.put(result.url, resumeFile, {
						headers: {
							'Content-Type': contentType,
						},
					});
				})
				.then(() => {
					navigate(
						'/two-factor/',
						{state: { applicationId: this.state.applicationId }},
					);
				})
				.catch((error) => {
					console.log(error);
					this.setState({ isError: true });
				});
		}
		else {
			axios.post(submitAppEndpoint,
				data, {
					headers: { 'content-type': 'application/json' },
				})
				.then((apiResult) => {
					navigate(
						'/two-factor/',
						{state: { applicationId: apiResult.data.id }},
					);
				})
				.catch((error) => {
					console.log(error);
					this.setState({ isError: true });
				});
		}
	}

	render () {
		const { isError } = this.state;
		const dialogProps = {
			open: this.state.isDialogOpened,
			onClose: isError ? this.closeDialog : undefined,
		};

		const waitingContent = <React.Fragment>
			<SpinnerContainer>
				<FaSpinner className='fa-spin' />
			</SpinnerContainer>
			Please wait while we process your application.<br />Do not leave or refresh this page.
		</React.Fragment>;
		const errorContent = 'We apologize for the inconvenience. An error seemed to have occurred and your submission did not go through. Please try again.';

		return <FormWrapper>
			<Dialog {...dialogProps}>
				<DialogContent>
					{isError ? errorContent : waitingContent}
				</DialogContent>
			</Dialog>

			<div className='form-container'>
				<Formik
					initialValues={{
						position: this.props.position,
						firstName: '',
						lastName: '',
						primaryPhoneNumber: '',
						emailAddress: '',
						address1: '',
						city: '',
						state: '',
						zipCode: '',
						currentEmployer: '',
						resume: '',
						linkedInUrl: '',
						githubUrl: '',
						portfolioUrl: '',
						otherUrl: '',
						hearAboutMk: '',
						other: '',
						workInSd: '',
						mkRightFit: '',
					}}
					onSubmit={this.onSubmit}
					validateOnBlur={true}
					validateOnChange={true}
					validationSchema={object().shape({
						firstName: firstNameValidation(true),
						lastName: lastNameValidation(true),
						primaryPhoneNumber: primaryPhoneNumberValidation(true),
						emailAddress: emailAddressValidation(true),
						address1: addressValidation(true),
						city: cityValidation(true),
						state: stateValidation(true),
						zipCode: postalCodeValidation(true),
						currentEmployer: currentEmployerValidation(true),
						resume: mixed().test('fileType', 'Please attach a valid .PDF, .DOC or .TXT file.', function (value) {
							if (value) {
								return SUPPORTED_FORMATS.includes(value.type);
							}
							return true;
						}),
						linkedInUrl: string(),
						githubUrl: string(),
						portfolioUrl: string(),
						otherUrl: string(),
						hearAboutMk: string(),
						other: string(),
						workInSd: string(),
						mkRightFit: string(),
					})}
				>
					<Form className='form'>
						<h2>Upload Resume</h2>
						<label
							htmlFor='resume'
							className='resume-label'
						>
							Attach Resume<sup>*</sup>
						</label>
						<div className='resume-button'>
							<FastField
								component={this.createFileInput}
								name='resume'
							/>
						</div>
						<div className='row'>
							<div className='col-12 col-lg-6'>
								<FastField
									component={FirstName}
									name='firstName'
									required={true}
								/>
							</div>
							<div className='col-12 col-lg-6'>
								<FastField
									component={LastName}
									name='lastName'
									required={true}
								/>
							</div>
						</div>
						<FastField
							component={PrimaryPhoneNumber}
							name='primaryPhoneNumber'
							required={true}
						/>
						<FastField
							component={EmailAddress}
							name='emailAddress'
							required={true}
						/>
						<FastField
							component={Address}
							name='address1'
							required={true}
						/>
						<FastField
							component={PostalCode}
							name='zipCode'
							required={true}
						/>
						<FastField
							component={City}
							name='city'
							required={true}
						/>
						<FastField
							component={State}
							name='state'
							required={true}
						/>
						<FastField
							component={CurrentEmployer}
							name='currentEmployer'
							required={true}
						/>
						<p className='input-section'>Links</p>
						<FastField
							component={LinkedInUrl}
							name='linkedInUrl'
						/>
						<FastField
							component={GitHubUrl}
							name='githubUrl'
						/>
						<FastField
							component={PortfolioUrl}
							name='portfolioUrl'
						/>
						<FastField
							component={OtherUrl}
							name='otherUrl'
						/>
						<p className='input-section'>Please answer the following questions:</p>
						<FastField
							component={HearAboutMk}
							name='hearAboutMk'
						/>
						<FastField
							component={OtherSpecify}
							name='other'
						/>
						<fieldset className='radioButtons'>
							<legend>Would you be able to work in MK Decision's San Diego, CA. office?</legend>
							<label>
								<Field name='workInSd' type='radio' value={true} /> Yes
							</label>
							<br />
							<label>
								<Field name='workInSd' type='radio' value={false} /> No
							</label>
						</fieldset>
						<FastField
							component={WhyMk}
							name='mkRightFit'
						/>
						<button className='primary-button animate-right' type='submit'>Submit Application<AngleRight /></button>
					</Form>
				</Formik>

				{/* <div className='form-shadow' /> */}
			</div>
		</FormWrapper>;
	}
}

export default ApplicationForm;
